import React, { useState, useRef } from 'react';
import { FiX, FiChevronLeft, FiChevronRight, FiZoomIn } from 'react-icons/fi';

const ImageGallery = ({ images = [] }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50, active: false });
  const containerRef = useRef(null);

  if (!images || images.length === 0) {
    return (
      <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
        <span className="text-muted">No Images</span>
      </div>
    );
  }

  const sorted = [...images].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
  const current = sorted[activeIdx];

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y, active: true });
  };

  const handleMouseLeave = () => {
    setZoomPos({ x: 50, y: 50, active: false });
  };

  const goNext = () => setActiveIdx((prev) => (prev + 1) % sorted.length);
  const goPrev = () => setActiveIdx((prev) => (prev - 1 + sorted.length) % sorted.length);

  return (
    <>
      {/* Main Image with Zoom */}
      <div
        ref={containerRef}
        className="position-relative rounded overflow-hidden mb-3"
        style={{
          height: 'clamp(250px, 50vw, 420px)',
          cursor: zoomPos.active ? 'zoom-out' : 'zoom-in',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={() => setLightboxOpen(true)}
      >
        <img
          src={current.url}
          alt={current.alt || 'Product'}
          className="w-100 h-100"
          style={{
            objectFit: 'contain',
            background: '#F9FAFB',
            transform: zoomPos.active ? 'scale(2)' : 'scale(1)',
            transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
            transition: zoomPos.active ? 'none' : 'transform 0.3s ease',
          }}
        />

        {/* Zoom hint */}
        {!zoomPos.active && (
          <div className="position-absolute d-flex align-items-center gap-1 px-2 py-1 rounded"
            style={{ bottom: '12px', right: '12px', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.75rem' }}>
            <FiZoomIn size={14} /> Hover to zoom
          </div>
        )}

        {/* Nav arrows */}
        {sorted.length > 1 && (
          <>
            <button
              className="btn btn-sm position-absolute border-0 d-flex align-items-center justify-content-center"
              style={{ top: '50%', left: '8px', transform: 'translateY(-50%)', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.85)' }}
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              <FiChevronLeft size={18} />
            </button>
            <button
              className="btn btn-sm position-absolute border-0 d-flex align-items-center justify-content-center"
              style={{ top: '50%', right: '8px', transform: 'translateY(-50%)', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.85)' }}
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              <FiChevronRight size={18} />
            </button>
          </>
        )}

        {/* Image counter */}
        {sorted.length > 1 && (
          <div className="position-absolute px-2 py-1 rounded"
            style={{ bottom: '12px', left: '12px', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.75rem' }}>
            {activeIdx + 1} / {sorted.length}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {sorted.length > 1 && (
        <div className="d-flex gap-2 overflow-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
          {sorted.map((img, idx) => (
            <button
              key={idx}
              className="border-0 p-0 rounded overflow-hidden flex-shrink-0"
              style={{
                width: '64px',
                height: '64px',
                border: idx === activeIdx ? '2px solid #B91C1C' : '2px solid transparent',
                opacity: idx === activeIdx ? 1 : 0.6,
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
              onClick={() => setActiveIdx(idx)}
            >
              <img src={img.url} alt={img.alt || ''} className="w-100 h-100" style={{ objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Lightbox */}
      {lightboxOpen && (
        <div
          className="position-fixed d-flex align-items-center justify-content-center"
          style={{ inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="btn position-absolute border-0 text-white"
            style={{ top: '16px', right: '16px', zIndex: 10000 }}
            onClick={() => setLightboxOpen(false)}
          >
            <FiX size={28} />
          </button>

          {sorted.length > 1 && (
            <>
              <button
                className="btn position-absolute border-0 text-white"
                style={{ top: '50%', left: '16px', transform: 'translateY(-50%)' }}
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
              >
                <FiChevronLeft size={36} />
              </button>
              <button
                className="btn position-absolute border-0 text-white"
                style={{ top: '50%', right: '16px', transform: 'translateY(-50%)' }}
                onClick={(e) => { e.stopPropagation(); goNext(); }}
              >
                <FiChevronRight size={36} />
              </button>
            </>
          )}

          <img
            src={sorted[activeIdx].url}
            alt={sorted[activeIdx].alt || 'Product'}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Lightbox thumbnails */}
          <div className="position-absolute d-flex gap-2 justify-content-center" style={{ bottom: '20px', left: 0, right: 0 }}>
            {sorted.map((img, idx) => (
              <button
                key={idx}
                className="border-0 p-0 rounded overflow-hidden"
                style={{
                  width: '48px', height: '48px',
                  border: idx === activeIdx ? '2px solid #EAB308' : '2px solid transparent',
                  opacity: idx === activeIdx ? 1 : 0.5,
                }}
                onClick={(e) => { e.stopPropagation(); setActiveIdx(idx); }}
              >
                <img src={img.url} alt="" className="w-100 h-100" style={{ objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
